// Lazy loading utilities for code splitting
import React, { lazy, Suspense } from "react";
import { LoadingSpinner } from "~/components/ui/LoadingSpinner";
import { performanceMonitor } from "./performance-monitor";

// 로딩 폴백 컴포넌트
interface LazyWrapperProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  className?: string;
}

export function LazyWrapper({
  children,
  fallback,
  className = "flex items-center justify-center min-h-[200px]",
}: LazyWrapperProps) {
  const defaultFallback = (
    <div className={className}>
      <LoadingSpinner size="lg" text="컴포넌트를 불러오는 중..." />
    </div>
  );

  // 코드 스플리팅 성능 추적
  React.useEffect(() => {
    const startTime = performance.now();

    return () => {
      const loadTime = performance.now() - startTime;
      performanceMonitor.addMetric({
        name: "lazy-component-load",
        value: loadTime,
        unit: "ms",
        timestamp: new Date(),
        type: "timing",
      });
    };
  }, []);

  return <Suspense fallback={fallback || defaultFallback}>{children}</Suspense>;
}

// 관리자 컴포넌트들 (큰 크기의 폼 컴포넌트들)
export const LazyRecipeForm = lazy(() =>
  import("~/components/admin/RecipeForm").then((module) => ({
    default: module.RecipeForm,
  }))
);

export const LazyArticleForm = lazy(() =>
  import("~/components/admin/ArticleForm").then((module) => ({
    default: module.ArticleForm,
  }))
);

export const LazyCacheMonitor = lazy(() =>
  import("~/components/admin/CacheMonitor").then((module) => ({
    default: module.CacheMonitor,
  }))
);

export const LazyBulkIngredientOperations = lazy(() =>
  import("~/components/admin/BulkIngredientOperations").then((module) => ({
    default: module.BulkIngredientOperations,
  }))
);

// 레시피 상세 컴포넌트 (큰 컴포넌트)
export const LazyRecipeDetail = lazy(() =>
  import("~/components/recipes/RecipeDetail").then((module) => ({
    default: module.RecipeDetail,
  }))
);

// 영양 정보 표시 컴포넌트 (차트 라이브러리 포함)
export const LazyNutritionDisplay = lazy(() =>
  import("~/components/recipes/NutritionDisplay").then((module) => ({
    default: module.NutritionDisplay,
  }))
);

// 리치 텍스트 에디터 (큰 라이브러리)
export const LazyRichTextEditor = lazy(() =>
  import("~/components/ui/RichTextEditor").then((module) => ({
    default: module.RichTextEditor,
  }))
);

// 차트 컴포넌트 (차트 라이브러리 포함)
export const LazyRadarChart = lazy(() =>
  import("~/components/ui/RadarChart").then((module) => ({
    default: module.RadarChart,
  }))
);

// 이미지 업로드 컴포넌트
export const LazyImageUpload = lazy(() =>
  import("~/components/ui/ImageUpload").then((module) => ({
    default: module.ImageUpload,
  }))
);

// 재료 자동완성 컴포넌트 (복잡한 로직 포함)
export const LazyIngredientAutocomplete = lazy(() =>
  import("~/components/recipes/IngredientAutocomplete").then((module) => ({
    default: module.IngredientAutocomplete,
  }))
);

// 쇼핑 관련 컴포넌트들
export const LazyShareModal = lazy(() =>
  import("~/components/shopping/ShareModal").then((module) => ({
    default: module.ShareModal,
  }))
);

export const LazyCoupangPurchaseButton = lazy(() =>
  import("~/components/shopping/CoupangPurchaseButton").then((module) => ({
    default: module.CoupangPurchaseButton,
  }))
);

// 이미지 라이트박스 (큰 라이브러리)
export const LazyImageLightbox = lazy(() =>
  import("~/components/recipes/ImageLightbox").then((module) => ({
    default: module.ImageLightbox,
  }))
);

// HOC for easier lazy component wrapping
export function withLazyWrapper<T = any>(
  LazyComponent: React.LazyExoticComponent<React.ComponentType<T>>,
  fallback?: React.ReactNode,
  className?: string
): React.ComponentType<T> {
  return function WrappedLazyComponent(props: T) {
    return (
      <LazyWrapper fallback={fallback} className={className}>
        <LazyComponent {...(props as any)} />
      </LazyWrapper>
    );
  };
}

// 특수한 경우를 위한 개별 래퍼들
export const WrappedLazyRecipeForm = withLazyWrapper(
  LazyRecipeForm,
  <div className="flex items-center justify-center min-h-[400px]">
    <LoadingSpinner size="lg" text="레시피 폼을 불러오는 중..." />
  </div>
);

export const WrappedLazyArticleForm = withLazyWrapper(
  LazyArticleForm,
  <div className="flex items-center justify-center min-h-[400px]">
    <LoadingSpinner size="lg" text="아티클 폼을 불러오는 중..." />
  </div>
);

export const WrappedLazyRecipeDetail = withLazyWrapper(
  LazyRecipeDetail,
  <div className="flex items-center justify-center min-h-[600px]">
    <LoadingSpinner size="lg" text="레시피 상세정보를 불러오는 중..." />
  </div>
);

export const WrappedLazyCacheMonitor = withLazyWrapper(
  LazyCacheMonitor,
  <div className="flex items-center justify-center min-h-[300px]">
    <LoadingSpinner size="lg" text="캐시 모니터를 불러오는 중..." />
  </div>
);
